import React, { useEffect, useReducer, useRef } from 'react';
import { useGetImportHistoryQuery } from '../../../../api/discovery';
import { useNotificationContext } from '../../../../providers/NotificationProvider';
import { IMPORT_TYPES } from '../../../../constants/discovery';

const SUCCESS_STATUS = 'success';
const FAILURE_STATUS = 'error';
const DATA_REFRESH_ON_SUCCESS_DELAY_MS = 2000;
const POLL_INTERVAL_MS = 5000;
const MAX_IMPORT_AGE_MS = 30 * 60 * 1000; // 30 mins

function uploadStateReducer(state, action) {
    switch (action.type) {
        case 'START_POLLING':
            return { isWaitingForUpload: true, isPolling: true };
        case 'RESOLVED':
            return { isWaitingForUpload: false, isPolling: false };
        default:
            return state;
    }
}

export const useUploadDetection = ({ orgId, onProcessingComplete, onProcessingFailed }) => {
    const [{ isWaitingForUpload, isPolling }, dispatch] = useReducer(uploadStateReducer, {
        isWaitingForUpload: false,
        isPolling: false,
    });

    const hasNotified = useRef(false);
    const { notifySuccess, notifyError } = useNotificationContext();

    const callbacksRef = useRef({ onProcessingComplete, onProcessingFailed });
    useEffect(() => {
        callbacksRef.current = { onProcessingComplete, onProcessingFailed };
    }, [onProcessingComplete, onProcessingFailed]);

    const { data: importHistory } = useGetImportHistoryQuery(
        { orgId },
        {
            skip: !orgId,
            pollingInterval: isPolling ? POLL_INTERVAL_MS : 0,
            refetchOnMountOrArgChange: true,
        },
    );

    // Runs on mount and on each poll. Inspects the most recent import entry:
    // - Still running, within 30 mins: shows the banner and starts polling.
    // - Still running, past 30 mins: stops polling silently (abandoned).
    // - Completed recently (caught while polling, or last_modified_date within one poll
    //   interval of page load): fires the success/failure notification and callbacks.
    // - Completed before this session: stops polling if active, otherwise no-ops.
    useEffect(() => {
        if (!importHistory?.content?.length) {
            return;
        }

        const entry = importHistory.content[0];

        if (!entry) {
            return;
        }

        const isCompleted = entry.status === SUCCESS_STATUS || entry.status === FAILURE_STATUS;

        if (isCompleted) {
            const completedRecently =
                isPolling ||
                (entry.last_modified_date &&
                    Date.now() - new Date(entry.last_modified_date).getTime() <= POLL_INTERVAL_MS);

            // Import completed before this session started
            if (!completedRecently) {
                if (isPolling) {
                    dispatch({ type: 'RESOLVED' });
                }
                return;
            }

            // Guard against double-firing across re-renders.
            if (hasNotified.current) {
                return;
            }
            hasNotified.current = true;

            if (entry.status === SUCCESS_STATUS) {
                // Delay before resolving so the backend has time to populate endpoints before the
                // data refresh fires. Banner removal, notification, and refresh all happen together.
                setTimeout(() => {
                    dispatch({ type: 'RESOLVED' });
                    notifySuccess({
                        message: 'Upload Successful',
                        description: 'RVTools data has been successfully added to inventory. Refreshing VMs...',
                    });
                    callbacksRef.current.onProcessingComplete?.();
                }, DATA_REFRESH_ON_SUCCESS_DELAY_MS);
            } else {
                dispatch({ type: 'RESOLVED' });
                notifyError({
                    message: 'Upload Failed',
                    description: entry.error_details
                        ? `An error occurred while processing the RVTools data: ${entry.error_details}`
                        : 'An error occurred while processing the RVTools data.',
                });
                callbacksRef.current.onProcessingFailed?.();
            }
        } else {
            // Abandon import if it has been running longer than the max age,
            // otherwise show the in-progress banner and start polling.
            const importAge = entry.created_at ? Date.now() - new Date(entry.created_at).getTime() : 0;

            if (importAge > MAX_IMPORT_AGE_MS) {
                dispatch({ type: 'RESOLVED' });
            } else {
                if (!isPolling) {
                    dispatch({ type: 'START_POLLING' });
                }
            }
        }
    }, [importHistory, isPolling, notifySuccess, notifyError]);

    return { isWaitingForUpload };
};
